import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  Button,
  Select,
  Input,
  DataTable,
  Skeleton,
  EmptyState,
  ErrorBoundary,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import {
  useSuppliers,
  useSupplierStats,
  SupplierStatusBadge,
  SupplierKpiCard,
  PAYMENT_TERMS_LABELS,
} from '@/features/suppliers';
import type { SupplierListItem } from '@/features/suppliers';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';

// ─── Constants ──────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

// ─── Icons ──────────────────────────────────────────────────────────────────

function UsersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  );
}

function CheckCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );
}

function DocumentTextIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  );
}

function SparklesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  );
}

function PlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function EyeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  );
}

// ─── Header ─────────────────────────────────────────────────────────────────

function SupplierHeader({ canCreate, onCreate }: { canCreate: boolean; onCreate: () => void }) {
  return (
    <div className="relative overflow-hidden bg-gradient-to-r from-teal-600 to-emerald-600 rounded-2xl p-6 text-white shadow-[0_20px_60px_-20px_rgba(13,148,136,0.45)] mb-6 animate-fade-in">
      <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-20 -left-10 h-48 w-48 rounded-full bg-black/10 blur-3xl" />
      <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white">Suppliers</h1>
          <p className="text-teal-50/90 mt-1 text-sm">
            Manage your vendor directory and procurement relationships
          </p>
        </div>
        {canCreate && (
          <Button
            variant="secondary"
            onClick={onCreate}
            className="bg-white/15 hover:bg-white/25 text-white border-white/30 backdrop-blur-sm"
          >
            <PlusIcon className="h-4 w-4 mr-1.5" />
            New Supplier
          </Button>
        )}
      </div>
    </div>
  );
}

// ─── Content ────────────────────────────────────────────────────────────────

function SupplierListContent() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { toast } = useToast();
  const profile = useAuthStore((s) => s.profile);

  const page = Number(searchParams.get('page') ?? '1');
  const search = searchParams.get('search') ?? '';
  const activeFilter = searchParams.get('is_active') ?? '';

  const roleLevel = ROLE_LEVELS[profile?.role ?? ''] ?? 0;
  // MANAGER, matching @Roles('MANAGER') on POST /suppliers — raising a PO for a
  // new vendor already creates the supplier record at that role.
  const canCreate = roleLevel >= ROLE_LEVELS.MANAGER;

  // Debounce search
  const [searchInput, setSearchInput] = useState(search);
  const searchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const handleSearch = useCallback(
    (value: string) => {
      setSearchInput(value);
      clearTimeout(searchTimerRef.current);
      searchTimerRef.current = setTimeout(() => {
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          if (value) {
            next.set('search', value);
          } else {
            next.delete('search');
          }
          next.set('page', '1');
          return next;
        });
      }, 300);
    },
    [setSearchParams],
  );

  function setParam(key: string, value: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value) {
        next.set(key, value);
      } else {
        next.delete(key);
      }
      next.set('page', '1');
      return next;
    });
  }

  function setPage(p: number) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('page', String(p));
      return next;
    });
  }

  const { data, isLoading, error } = useSuppliers({
    page,
    limit: 20,
    search: search || undefined,
    is_active: activeFilter ? activeFilter === 'true' : undefined,
  });

  const { data: stats } = useSupplierStats();

  useEffect(() => {
    if (error) {
      toast({ title: (error as Error).message, variant: 'error' });
    }
  }, [error]);

  // Keyboard shortcuts
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
      if (e.key === '/') {
        e.preventDefault();
        const el = document.querySelector<HTMLInputElement>('input[placeholder*="Search supplier"]');
        el?.focus();
      }
      if (e.key === 'n' && canCreate) {
        e.preventDefault();
        navigate('/inventory/suppliers/new');
      }
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navigate, canCreate]);

  const suppliers = data?.data ?? [];
  const total = data?.meta?.total ?? 0;

  const columns: ColumnDef<SupplierListItem>[] = [
    {
      key: 'name',
      header: 'Supplier Name',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/inventory/suppliers/${row.id}`)}
          className="text-sm text-teal-600 dark:text-teal-400 hover:underline text-left font-medium"
        >
          {row.name}
        </button>
      ),
    },
    {
      key: 'contact_person',
      header: 'Contact Person',
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">
          {row.contact_person || '\u2014'}
        </span>
      ),
    },
    {
      key: 'phone',
      header: 'Phone',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 tabular-nums">
          {row.phone || '\u2014'}
        </span>
      ),
    },
    {
      key: 'gstin',
      // Neutral: suppliers can be registered in different countries (I-3), so
      // the column holds GSTINs, TRNs, VATINs and foreign VAT numbers alike.
      header: 'Tax ID',
      render: (_v, row) => (
        <span className="text-sm text-neutral-600 dark:text-neutral-400 font-mono tracking-wide">
          {row.gstin || '\u2014'}
        </span>
      ),
    },
    {
      key: 'payment_terms',
      header: 'Payment Terms',
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-300">
          {PAYMENT_TERMS_LABELS[row.payment_terms] ?? row.payment_terms}
        </span>
      ),
    },
    {
      key: 'is_active',
      header: 'Status',
      render: (_v, row) => <SupplierStatusBadge isActive={row.is_active} />,
    },
    {
      key: 'actions',
      header: '',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => navigate(`/inventory/suppliers/${row.id}`)}
          className="p-1.5 rounded-lg text-neutral-500 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
          title="View"
        >
          <EyeIcon className="h-4 w-4" />
        </button>
      ),
    },
  ];

  // ── Loading skeleton ──────────────────────────────────────────────────────

  if (isLoading && !data) {
    return (
      <>
        <Skeleton variant="rect" height={96} className="rounded-xl mb-6" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={120} className="rounded-xl" />
          ))}
        </div>
        <Skeleton variant="rect" height={56} className="rounded-xl mb-4" />
        <Skeleton variant="rect" height={400} className="rounded-xl" />
      </>
    );
  }

  // ── Empty state (no filters applied) ──────────────────────────────────────

  if (!isLoading && total === 0 && !activeFilter && !search) {
    return (
      <>
        <SupplierHeader canCreate={canCreate} onCreate={() => navigate('/inventory/suppliers/new')} />
        <EmptyState
          variant="page"
          icon={<UsersIcon className="h-full w-full" />}
          title="No suppliers yet"
          description="Add your first supplier to build a vendor directory for streamlined procurement."
          action={
            canCreate
              ? { label: 'Add Supplier', onClick: () => navigate('/inventory/suppliers/new') }
              : undefined
          }
        />
      </>
    );
  }

  return (
    <>
      <SupplierHeader canCreate={canCreate} onCreate={() => navigate('/inventory/suppliers/new')} />

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6 mb-6">
        <SupplierKpiCard
          title="Total Suppliers"
          value={(stats?.total ?? 0).toLocaleString('en-IN')}
          tone="teal"
          icon={<UsersIcon className="h-5 w-5" />}
        />
        <SupplierKpiCard
          title="Active"
          value={(stats?.active ?? 0).toLocaleString('en-IN')}
          tone="success"
          icon={<CheckCircleIcon className="h-5 w-5" />}
        />
        <SupplierKpiCard
          title="With Tax ID"
          value={(stats?.with_gstin ?? 0).toLocaleString('en-IN')}
          tone="brand"
          icon={<DocumentTextIcon className="h-5 w-5" />}
        />
        <SupplierKpiCard
          title="New This Month"
          value={(stats?.new_this_month ?? 0).toLocaleString('en-IN')}
          tone="violet"
          icon={<SparklesIcon className="h-5 w-5" />}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="w-56">
          <Input
            placeholder="Search supplier..."
            value={searchInput}
            onChange={(v) => handleSearch(v)}
          />
        </div>
        <div className="w-44">
          <Select
            options={STATUS_OPTIONS}
            value={activeFilter}
            onChange={(v) => setParam('is_active', typeof v === 'string' ? v : '')}
            placeholder="All statuses"
          />
        </div>
      </div>

      {/* Data Table */}
      <div className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm rounded-xl shadow-md animate-slide-up">
        <DataTable<SupplierListItem>
          columns={columns}
          data={suppliers}
          loading={isLoading}
          totalCount={total}
          currentPage={page}
          pageSize={20}
          onPageChange={setPage}
          emptyState={{
            title: 'No suppliers match filters',
            description: 'Try adjusting your search or status filter',
          }}
        />
      </div>
    </>
  );
}

// ─── Page export ────────────────────────────────────────────────────────────

export function SupplierListPage() {
  return (
    <PageContainer
      breadcrumbs={[
        { label: 'Inventory', href: '/inventory' },
        { label: 'Suppliers' },
      ]}
    >
      <ErrorBoundary>
        <SupplierListContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
